// ====================================================================
// THE BURIED COINS — Wizard Zip's digging quest in the west meadow
// ====================================================================
// The game's first quest: Wizard Zip — a tall, blue Chocky in a wizard hat
// and robe (a Chocky-model NPC, src/game/chockyNpc.js) — stands by the west
// road. Talk to him (the same bump-zone speech bubble everything else uses)
// and a dirt patch with a big red ✕ appears out in his meadow. Digging is
// jumping: every real landing on the ✕ (main.js calls onPlayerLand from the
// player's landing branch) counts as one scoop — dirt flies, the hole
// deepens, the spoil mound grows — and the third scoop breaks his old spell
// and pops the enchanted coins out of the ground as one-shot collectibles
// (ctx.spawnQuestCoins builds coins that never respawn) plus a wardrobe
// reward: the Lucky Hat, the quest-only item in the store catalog.
//
// State ('digging' | 'done') persists via game/save.js, so the dug-open hole
// stays dug across reloads. The mid-quest scoop count deliberately doesn't
// persist — re-doing two jumps is play, not loss.
//
// Built through a ctx from main.js (the zone/store pattern): world builders,
// bump zones, FX, and the wallet stay main.js-owned; this module owns Zip,
// the marker meshes, and the quest state machine. Zip registers in the actor
// registry like every other mover (he idles and turns — that counts).

import * as THREE from 'three';
import { loadSave, patchSave } from './save.js';
import { equip, getEquipped } from './outfits.js';
import { heightAt } from '../world/heightfield.js';
import { spawnActor, despawnActor } from '../world/actors.js';
import { buildChockyNpc } from './chockyNpc.js';
import { markDynamic } from '../world/freezeStatics.js';
import { createWizardSpellFx } from './wizardSpellFx.js';

const QUEST_ID = 'dig';
const GIVER = { x: -14.5, z: 4.5 };
const DIG = { x: -19, z: 8.5 };
const DIGS_NEEDED = 3;
const DIG_RADIUS = 1.15;          // landing this close to the ✕ counts

let state = null;                 // null (not started) | 'digging' | 'done'
function setState(s) {
  state = s;
  patchSave({ quests: { ...loadSave().quests, [QUEST_ID]: s } });
}

// ---- Wizard Zip: a blue, taller Chocky in the wizard outfit (hat + robe +
// sleeves), playing the baked Idle so he breathes like the player. A real
// Chocky-model NPC — src/game/chockyNpc.js builds and grounds it; here we
// just say how he should look. (The scale is uniform on purpose — see the
// note in chockyNpc.js; the height comes from being bigger plus the hat.)
const ZIP_TINT = 0x86b4ec;   // friendly periwinkle "blue skin"
const ZIP_SCALE = 1.22;      // a bit taller / bigger than the player
const ZIP_OUTFIT = ['wizard_hat', 'wizard_robe', 'wizard_sleeve_l', 'wizard_sleeve_r'];

// Conform a flat disc to the terrain around the dig spot — the ground is
// heightAt(x, z), never flat, so the patch hugs the meadow's slope.
function conformedDisc(r, segs, lift, color, baseY) {
  const geo = new THREE.CircleGeometry(r, segs);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, heightAt(DIG.x + pos.getX(i), DIG.z + pos.getZ(i)) - baseY + lift);
  }
  geo.computeVertexNormals();
  return new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color }));
}

export function initQuest(ctx) {
  state = loadSave().quests[QUEST_ID] || null;
  let digs = 0;
  let zapLookX = GIVER.x;
  let zapLookZ = GIVER.z;
  let zapLookTimer = 0;

  // ---- Zip's field: hay bales (collidable — they're jumpable), wildflowers,
  // and a signpost where the meadow leaves the west road.
  const baleAt = (x, z, w, h, d, color) => ctx.addBox(x, heightAt(x, z) + h / 2, z, w, h, d, color, true);
  baleAt(-12.6, 6.6, 0.95, 0.55, 0.65, 0xd8b35a);
  baleAt(-13.5, 7.5, 0.9, 0.5, 0.6, 0xcfa84e);
  baleAt(-17.6, 11.2, 0.95, 0.55, 0.6, 0xd8b35a);
  ctx.buildFlowerPatch(-16.6, 6.0, 6, 0.8);
  ctx.buildFlowerPatch(-13.0, 9.4, 5, 0.7);
  ctx.buildFlowerPatch(-21.2, 7.0, 6, 0.8);
  ctx.buildSignpost(-10.8, -1.7, [{ text: "WIZARD'S MEADOW", yaw: -0.6 }]);

  // ---- Wizard Zip himself (a Chocky-model NPC).
  const wizard = buildChockyNpc({
    parent: ctx.propsGroup, x: GIVER.x, z: GIVER.z,
    tint: ZIP_TINT, scale: ZIP_SCALE,
    outfitModel: 'wizard', outfitPieces: ZIP_OUTFIT,
  });
  // Rest pose: facing back toward the village crossroads.
  const REST_YAW = Math.atan2(-GIVER.x, -GIVER.z);
  let yaw = REST_YAW;
  wizard.group.rotation.y = yaw;
  wizard.actor = spawnActor('wizard', wizard);
  const spellFx = createWizardSpellFx({
    parent: ctx.propsGroup,
    x: GIVER.x,
    z: GIVER.z,
    groundY: wizard.group.position.y,
    getYaw: () => yaw,
  });

  // ---- The dig spot: dirt patch, red ✕, the hole, and the spoil mound.
  const digBaseY = heightAt(DIG.x, DIG.z);
  const marker = new THREE.Group();
  markDynamic(marker);   // digging animates the hole/mound/✕ inside
  marker.position.set(DIG.x, digBaseY, DIG.z);
  const patch = conformedDisc(1.25, 20, 0.03, 0x9c7a52, digBaseY);
  marker.add(patch);
  const hole = conformedDisc(0.6, 14, 0.05, 0x4e3826, digBaseY);
  marker.add(hole);
  const xGroup = new THREE.Group();
  const xMat = new THREE.MeshLambertMaterial({ color: 0xd84840 });
  for (const a of [Math.PI / 4, -Math.PI / 4]) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.045, 0.22), xMat);
    bar.rotation.y = a;
    bar.position.y = 0.07;
    xGroup.add(bar);
  }
  marker.add(xGroup);
  const mound = new THREE.Mesh(
    new THREE.SphereGeometry(0.3, 8, 6),
    new THREE.MeshLambertMaterial({ color: 0x6e5238 })
  );
  mound.position.set(1.0, 0.05, 0.45);
  marker.add(mound);
  ctx.propsGroup.add(marker);

  function refreshMarker() {
    marker.visible = state !== null;
    xGroup.visible = state === 'digging';
    const t = state === 'done' ? 1 : digs / DIGS_NEEDED;
    hole.visible = t > 0;
    hole.scale.set(0.3 + 0.7 * t, 1, 0.3 + 0.7 * t);
    mound.visible = t > 0;
    const m = 0.5 + t * 0.8;
    mound.scale.set(m, m * 0.5, m);
  }
  refreshMarker();

  // ---- Talking to Zip: sticky speech bubble while you stand with him.
  // Quests have no accept button — saying hello starts it (kid-proof).
  ctx.addBumpZone(GIVER.x, GIVER.z, 1.7, 'wizard-zip', (sourceId) => {
    if (!state) {
      setState('digging');
      refreshMarker();
      ctx.showBumpMsg('🧙 WIZARD ZIP', "Greetings, Chocky! Long ago I hid a pouch of enchanted coins beneath the big red ✕ in my meadow. Hop up and down on it — boing, boing — to break my old spell and dig 'em free!", 0, sourceId);
    } else if (state === 'digging') {
      ctx.showBumpMsg('🧙 WIZARD ZIP', 'See the big red ✕ out in the meadow? Jump on it — boing, boing, boing — and my enchanted coins will pop right up!', 0, sourceId);
    } else {
      ctx.showBumpMsg('🧙 WIZARD ZIP', "You broke the spell and freed my coins! That lucky hat looks splendid on a young mage like you ✨", 0, sourceId);
    }
  }, (sourceId) => ctx.hideBumpMsg(sourceId));

  function completeDig() {
    setState('done');
    refreshMarker();
    ctx.spawnQuestCoins(DIG.x, DIG.z);
    ctx.addCamShake(0.3);
    ctx.triggerFace('grin', 1.5);
    // The wardrobe reward: own the Lucky Hat, and wear it if bare-headed
    // (never knock off a hat the kid paid for).
    const owned = new Set(loadSave().owned);
    owned.add('straw');
    patchSave({ owned: [...owned] });
    if (!getEquipped().head) equip('head', 'straw');
    ctx.showBumpMsg('🪙 TREASURE!', "Wizard Zip's enchanted coins! His LUCKY HAT is yours too — it's waiting in Chocky's Closet.", 8, 'dig-spot');
  }

  // Called by main.js from the player's landing branch — a real landing
  // (genuinely airborne, real impact), not a downhill micro-step.
  function onPlayerLand(x, z) {
    if (state !== 'digging') return;
    if (Math.hypot(x - DIG.x, z - DIG.z) > DIG_RADIUS) return;
    digs++;
    ctx.spawnDust(DIG.x, digBaseY + 0.05, DIG.z, 12 + digs * 4, 1.2 + digs * 0.2);
    ctx.addCamShake(0.1 + digs * 0.04);
    if (digs >= DIGS_NEEDED) { completeDig(); return; }
    refreshMarker();
    ctx.showBumpMsg('✨ DIG DIG DIG!',
      digs === 1 ? "Something's glowing down there! Jump on the ✕ again!" : 'Almost! One more BIG hop!',
      2.5, 'dig-spot');
  }

  // Per-frame (play mode): Zip idles (baked clip) and turns to watch the
  // player; when he has just zapped a wolf, that aim takes priority for a
  // short beat. The ✕ pulses so it reads as "the spot". No allocation in here.
  function update(dt, t) {
    const p = ctx.getPlayerPos();
    const dx = p.x - GIVER.x, dz = p.z - GIVER.z;
    if (dx * dx + dz * dz > 1600) {
      spellFx.update(dt, t, false);
      return;                                  // asleep past 40 units, like the oxen
    }
    wizard.update(dt);                        // tick his idle mixer (breathing/fidget)
    if (zapLookTimer > 0) zapLookTimer -= dt;
    const want = zapLookTimer > 0
      ? Math.atan2(zapLookX - GIVER.x, zapLookZ - GIVER.z)
      : ((dx * dx + dz * dz < 49) ? Math.atan2(dx, dz) : REST_YAW);
    let diff = want - yaw;
    while (diff > Math.PI) diff -= 2 * Math.PI;
    while (diff < -Math.PI) diff += 2 * Math.PI;
    yaw += diff * Math.min(1, dt * (zapLookTimer > 0 ? 10 : 3));
    wizard.group.rotation.y = yaw;
    spellFx.update(dt, t, true);
    if (state === 'digging') {
      const s = 1 + Math.sin(t * 3) * 0.07;
      xGroup.scale.set(s, 1, s);
    }
  }

  function getWizardZapOrigin(out) {
    out.set(GIVER.x, wizard.group.position.y + 1.36, GIVER.z);
    return out;
  }

  function aimWizardAt(x, z) {
    zapLookX = x;
    zapLookZ = z;
    zapLookTimer = 0.45;
  }

  function dispose() { despawnActor(wizard.actor); }

  // Wayfinding beacon for the minimap compass (src/game/minimap.js): while the
  // dig is underway, point at the ✕; otherwise nothing (unstarted/finished
  // quests don't clutter the map).
  function getBeacon() {
    return state === 'digging' ? { x: DIG.x, z: DIG.z, label: 'Dig for treasure' } : null;
  }

  return { update, onPlayerLand, wizardPosition: GIVER, getWizardZapOrigin, aimWizardAt, getBeacon, dispose };
}
